'use client'

import {
  forwardRef,
  useRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode
} from 'react'

import IconCircleClose from 'asset/icon/circle_close.svg'
import { useComposedRefs } from 'hook/useComposedRefs'
import cn from 'util/cn'

interface ITextAreaAutoResize
  extends Omit<ComponentPropsWithoutRef<'textarea'>, 'prefix'> {
  maxRows?: number
  prefix?: ReactNode
  suffix?: ReactNode
  backgroundColor?: string
  textareaClassName?: string
  showClear?: boolean
  onClear?: () => void
  onEnter?: () => void
}
const TextAreaAutoResize = forwardRef<
  ElementRef<'textarea'>,
  ITextAreaAutoResize
>((props, ref) => {
  const {
    maxRows = 1,
    prefix,
    suffix,
    backgroundColor = 'var(--background-b1)',
    showClear = false,
    value,
    onClear,
    className,
    textareaClassName,
    onEnter,
    ...rest
  } = props

  const textRef = useRef<HTMLTextAreaElement | null>(null)
  const composeRefs = useComposedRefs(ref, textRef)

  const handleKeypress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 修復中文輸入法的問題
    if (event.nativeEvent.isComposing) return
    if (event.code === 'Enter' && (!event.shiftKey || maxRows === 1)) {
      event.preventDefault() // Prevent default behavior of the enter key
      onEnter?.()
    }
  }

  const wrapperStyle = {
    '--background-color': backgroundColor
  } as React.CSSProperties

  return (
    <div
      className={cn(
        'flex items-center rounded-[12px] bg-[--background-color] px-[11px] py-[7px]',
        className
      )}
      style={wrapperStyle}
    >
      {prefix && (
        <div className='flex items-center whitespace-nowrap pr-[12px]'>
          {prefix}
        </div>
      )}

      <textarea
        ref={composeRefs}
        value={value}
        onKeyDown={handleKeypress}
        rows={1}
        {...rest}
        className={cn(
          'flex-grow-1 w-full flex-shrink resize-none overflow-y-hidden border-none bg-transparent p-[4px] text-md outline-none',
          'cursor-text placeholder-label-l2 placeholder:text-md',
          maxRows === 1 && 'break-normal',
          textareaClassName
        )}
      />
      {suffix && (
        <div className='flex items-center whitespace-nowrap'>{suffix}</div>
      )}
      {showClear && value && (
        <div
          className='ml-[4px] flex cursor-pointer items-center whitespace-nowrap'
          onClick={onClear}
        >
          <IconCircleClose className='h-[18px] w-[18px] fill-label-l2' />
        </div>
      )}
    </div>
  )
})

export default TextAreaAutoResize
